import React, { useState } from 'react';

import { Link } from '~/lib/react-router-dom';

import { Box, Button, Stack, Typography } from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';

import GridPagination from '~/components/Common/Elements/GridPagination';

import { updateAgencyUsersTier } from '~/services/AgentHub/AgentService';

import { Agency, EoiStatusValue, MappedAgent, Tier } from '~/types/services/agentHub';

import EoiStatusChip from '../../EoiStatusChip';
import EditTierForm from '../../Tier/EditTierForm';

const DEFAULT_SIZE_PER_PAGE = 10;
const YES_LABEL = 'Yes';
const NO_LABEL = 'No';

interface AgencyDetailsProps {
  data: Agency;
  users: Array<MappedAgent>;
  page: number;
  setPage: (page: number) => void;
  total: number;
  loadingUserState: Utils.FetchingState;
}

export default function AgencyDetails({ data, users, total, loadingUserState }: AgencyDetailsProps) {
  const [page, setPage] = useState(0);
  const [tier, setTier] = useState<Tier>('tier1');
  const [showEditTierForm, setShowEditTierForm] = useState(false);

  const columns: Array<GridColDef<MappedAgent>> = [
    { field: 'name', headerName: 'Name', width: 160, sortable: false, display: 'flex' },
    { field: 'email', headerName: 'Email', width: 200, sortable: false, display: 'flex' },
    {
      field: 'status',
      headerName: 'Status',
      align: 'center',
      width: 120,
      sortable: false,
      display: 'flex',
      renderCell: (params) => <EoiStatusChip status={params.row.status as EoiStatusValue} />,
    },
    {
      field: 'links',
      headerName: 'Links',
      width: 100,
      sortable: false,
      renderCell: (params) => {
        return (
          <>
            {params.row.customerId && (
              <Link target="_blank" rel="noreferrer" to={`/users/${params.row.customerId}`}>
                User
              </Link>
            )}
            {params.row.eoiSubmissionId && (
              <Box ml={2}>
                <Link
                  target="_blank"
                  rel="noreferrer"
                  to={`/users-list/agency-submission/${params.row.eoiSubmissionId}`}
                >
                  EOI
                </Link>
              </Box>
            )}
          </>
        );
      },
      display: 'flex',
    },
    {
      field: 'isAgencyAdmin',
      headerName: 'Agency admin',
      width: 130,
      sortable: false,
      display: 'flex',
      align: 'center',
      renderCell: (params) => {
        return params.row.isAgencyAdmin ?? false ? YES_LABEL : NO_LABEL;
      },
    },
    {
      field: 'isAffiliationAdmin',
      headerName: 'Affiliation admin',
      width: 150,
      sortable: false,
      display: 'flex',
      align: 'center',
      renderCell: (params) => {
        return params.row.isAffiliationAdmin ?? false ? YES_LABEL : NO_LABEL;
      },
    },
  ];

  async function onUpdateTier() {
    await updateAgencyUsersTier(data.id, tier);
  }

  return (
    <>
      <Box display="grid" gap={2} gridTemplateColumns="1fr 1fr" sx={{ minWidth: '500px' }}>
        <Stack>
          <Typography>Name:</Typography>
          <Typography fontWeight="bold">{data.name}</Typography>
        </Stack>
        <Stack>
          <Typography>Identity:</Typography>
          <Typography fontWeight="bold">
            {data.businessCodeType} - {data.businessCodeValue}
          </Typography>
        </Stack>
        <Stack>
          <Typography sx={{ gridColumn: 'span 2' }}>Affiliation:</Typography>
          <Typography sx={{ gridColumn: 'span 2' }} fontWeight="bold">
            {data.affiliation}
          </Typography>
        </Stack>

        <Typography mt={2} variant="h6" sx={{ gridColumn: 'span 2' }}>
          Address
        </Typography>
        <Stack>
          <Typography sx={{ gridColumn: 'span 2' }}>Street:</Typography>
          <Typography fontWeight="bold">{data.address}</Typography>
        </Stack>
        <Stack>
          <Typography>City:</Typography>
          <Typography fontWeight="bold">{data.city}</Typography>
        </Stack>
        <Stack>
          <Typography>Postal Code:</Typography>
          <Typography fontWeight="bold">{data.postcode}</Typography>
        </Stack>
        <Stack>
          <Typography>State:</Typography>
          <Typography fontWeight="bold">{data.state}</Typography>
        </Stack>
        <Stack>
          <Typography>Country:</Typography>
          <Typography fontWeight="bold">{data.country}</Typography>
        </Stack>

        <Stack>
          {!showEditTierForm && (
            <Button variant="outlined" color="primary" onClick={() => setShowEditTierForm(true)}>
              Change agents tier
            </Button>
          )}

          {showEditTierForm && (
            <EditTierForm
              onSubmit={onUpdateTier}
              setShowEditTierForm={setShowEditTierForm}
              setTier={setTier}
              tier={tier}
            />
          )}
        </Stack>
      </Box>

      <Typography mt={2} variant="h6">
        Agents
      </Typography>

      <Box sx={{ overflowX: 'auto' }}>
        <DataGrid
          rows={users}
          rowCount={total}
          columns={columns}
          loading={loadingUserState == 'loading'}
          pagination
          paginationMode="server"
          paginationModel={{ page: page, pageSize: DEFAULT_SIZE_PER_PAGE }}
          pageSizeOptions={[DEFAULT_SIZE_PER_PAGE]}
          onPaginationModelChange={({ page }) => setPage(page)}
          slots={{ pagination: GridPagination }}
          disableColumnFilter
          disableColumnMenu
          disableRowSelectionOnClick
          autoHeight
        />
      </Box>
    </>
  );
}
